import React from "react";
import { toTitleCaseAdvanced } from "../../helpers/Helper";
import Link from "next/link";
import styles from "./CatalogHeader.module.css";
import { useAppContext } from "@components/Provider";
import Image from "next/image";

// Chevron icon for collapsible sections
const ChevronIcon = ({ isOpen }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${styles.chevronIcon} ${isOpen ? styles.rotated : ""}`}
    style={{
      marginLeft: "6px",
    }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#222"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Category icon component that uses imageUrl from response
const CategoryIcon = ({ category }) => {
  // If category has imageUrl, use it
  if (category && category.imageUrl) {
    return (
      <Image
        src={category.imageUrl}
        alt={category.label || category.name || category.Name || "Category"}
        width={16}
        height={16}
        style={{ marginRight: "8px" }}
      />
    );
  }

  // Return null if no imageUrl
  return null;
};

const CatalogHeader = (props) => {
  const { data } = useAppContext();
  let { slug, setIsOpen } = props;
  const [selectedTab, setSelectedTab] = React.useState(null);

  // Get category tree data from context instead of API call
  const categoryTreeData = data?.CategoryTree || [];
  const loading = false; // No loading state needed since data comes from context

  // Generic function to check if a category is active
  const isCategoryActive = (category, slug) => {
    if (!slug) return false;

    const slugPath = slug.split("?")[0].split("/");
    const categorySlug = slugPath.pop()?.toLowerCase();

    if (!categorySlug || decodeURI(categorySlug) === "search") return false;

    const categoryName = (category.label || category.name || category.Name || "").toLowerCase();
    return decodeURI(categorySlug) === categoryName;
  };

  // Handle tab selection
  const handleTabSelect = (category) => {
    if (selectedTab && selectedTab._id === category._id) {
      // If clicking the same tab, close it
      setSelectedTab(null);
    } else {
      // Select the new tab
      setSelectedTab(category);
    }
  };

  // Render subcategories for the selected category in 4-column grid layout
  const renderCategoryColumn = (category) => {
    if (!category || !category.children) return null;

    // Create a grid layout with 4 columns per row
    const items = category.children;
    const itemsPerRow = 4;

    // Group items into rows of 4
    const rows = [];
    for (let i = 0; i < items.length; i += itemsPerRow) {
      const row = items.slice(i, i + itemsPerRow);
      rows.push(row);
    }

    return (
      <div
        key={category._id || category.name || category.Name}
        className={styles.categoriesContainer}
      >
        {rows.map((rowItems, rowIndex) => (
          <div key={rowIndex} className={styles.row}>
            {rowItems.map((subCategory, columnIndex) => {
              const subCategoryName = toTitleCaseAdvanced(
                (
                  subCategory.label ||
                  subCategory.name ||
                  subCategory.Name ||
                  "Subcategory"
                ).toString()
              );

              return (
                <div
                  key={subCategory._id || subCategory.name || subCategory.Name || columnIndex}
                  className={styles.categorySection}
                >
                  <h4 className={styles.sectionTitle}>
                    <CategoryIcon category={subCategory} />
                    {subCategoryName}
                  </h4>
                  {subCategory.children && subCategory.children.length > 0 ? (
                    <div className={styles.subcategoryList}>
                      {subCategory.children.map((childCategory) => {
                        const childCategoryName = toTitleCaseAdvanced(
                          (
                            childCategory.label ||
                            childCategory.name ||
                            childCategory.Name ||
                            "Child"
                          ).toString()
                        );
                        const isChildActive = isCategoryActive(childCategory, slug);

                        return (
                          <Link
                            key={childCategory._id || childCategory.name || childCategory.Name}
                            href={`/category/${childCategoryName.toLowerCase()}`}
                            className={`${styles.subcategoryLink} ${isChildActive ? styles.activeSubcategory : ""}`}
                            onClick={() => setIsOpen(false)}
                          >
                            {childCategoryName}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {/* Fill remaining columns with empty spaces to maintain 4-column layout */}
            {Array.from({ length: 4 - rowItems.length }, (_, index) => (
              <div key={`empty-${index}`} className={styles.emptyColumn}></div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // Render tab buttons
  const renderTabButton = (category) => {
    const categoryName = toTitleCaseAdvanced(
      (category.label || category.name || category.Name || "Category").toString()
    );
    const isSelected = selectedTab && selectedTab._id === category._id;
    const hasChildren = category.children && category.children.length > 0;

    return (
      <div key={category._id || category.name || category.Name} className={styles.tabButton}>
        <div
          className={`${styles.tabHeader} ${isSelected ? styles.selectedTab : ""}`}
          onClick={() => handleTabSelect(category)}
        >
          <CategoryIcon category={category} />
          <span className={styles.tabTitle}>{categoryName}</span>
          {hasChildren ? <ChevronIcon isOpen={isSelected} /> : null}
        </div>
      </div>
    );
  };

  // Use category tree data from context
  const displayCategories = categoryTreeData;

  if (loading) {
    return (
      <div className={styles.menuContainer}>
        <div className={styles.loadingMessage}>Loading categories...</div>
      </div>
    );
  }

  return (
    <div className={styles.menuContainer}>
      {displayCategories.length === 0 && (
        <div className={styles.noCategoriesMessage}>No categories found in API response</div>
      )}
      <div className={styles.tabLayout}>
        <div className={styles.tabButtons}>
          {displayCategories.map((category) => renderTabButton(category))}
        </div>
        {selectedTab ? (
          <div className={`${styles.tabContent} ${styles.active}`}>
            <div className={styles.categoriesGrid}>{renderCategoryColumn(selectedTab)}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CatalogHeader;
